import React, { useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheck, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import BulkIdentifierInput from '../BulkIdentifierInput';

const getReceivingServiceLabels = (requirements = {}, hasExpectedSerialValidation = false) => {
  const labels = [];
  if (requirements.requireSerialNumbers === true || hasExpectedSerialValidation) labels.push('Record serial numbers');
  if (requirements.requireMacAddresses === true) labels.push('Record MAC addresses');
  return labels;
};

const SerialNumberModal = ({
  isOpen,
  onClose,
  lineItem,
  onReceive,
  onHold,
  isLoading,
  identifierRequirements = {}
}) => {
  const [enteredSerials, setEnteredSerials] = useState([]);
  const [enteredMacs, setEnteredMacs] = useState([]);
  const [receivedQuantity, setReceivedQuantity] = useState('');
  const [isOverride, setIsOverride] = useState(false);

  const expectedSerials = useMemo(
    () => lineItem?.assets?.map((asset) => asset.serialNumber?.toLowerCase()).filter(Boolean) || [],
    [lineItem]
  );

  const hasExpectedSerialValidation = expectedSerials.length > 0;
  const requireSerials = identifierRequirements.requireSerialNumbers === true || hasExpectedSerialValidation;
  const requireMacs = identifierRequirements.requireMacAddresses === true;
  const canOverrideValidation = hasExpectedSerialValidation && identifierRequirements.requireSerialNumbers !== true;

  const availableQuantity = useMemo(() => {
    if (lineItem?.assets?.length) {
      return lineItem.assets.length;
    }

    return Math.max(0, (lineItem?.qtyOrdered || 0) - (lineItem?.qtyReceived || 0) - (lineItem?.qtyOnHold || 0));
  }, [lineItem]);

  const normalizedEnteredSerials = useMemo(
    () => enteredSerials.map((value) => String(value || '').trim()).filter(Boolean),
    [enteredSerials]
  );
  const normalizedEnteredMacs = useMemo(
    () => enteredMacs.map((value) => String(value || '').trim()).filter(Boolean),
    [enteredMacs]
  );

  const validationStatus = useMemo(() => {
    const status = {};
    normalizedEnteredSerials.forEach((serial) => {
      status[serial] = expectedSerials.includes(serial.toLowerCase());
    });
    return status;
  }, [normalizedEnteredSerials, expectedSerials]);

  useEffect(() => {
    if (!isOpen) return;
    setEnteredSerials([]);
    setEnteredMacs([]);
    setReceivedQuantity('');
    setIsOverride(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const parsedQuantity = Number.parseInt(receivedQuantity, 10);
  const requestedQuantity = Number.isInteger(parsedQuantity) && parsedQuantity > 0
    ? parsedQuantity
    : availableQuantity;
  const quantityIsValid = requestedQuantity > 0 && requestedQuantity <= availableQuantity;

  const serialCountMatches = normalizedEnteredSerials.length === requestedQuantity;
  const macCountMatches = normalizedEnteredMacs.length === requestedQuantity;
  const serialsValidAgainstExpected = normalizedEnteredSerials.every((serial) => validationStatus[serial]);

  const serialRequirementMet = !requireSerials || (
    serialCountMatches &&
    (!hasExpectedSerialValidation || isOverride || serialsValidAgainstExpected)
  );
  const macRequirementMet = !requireMacs || macCountMatches;
  const canReceive = quantityIsValid && serialRequirementMet && macRequirementMet;
  const canHold = hasExpectedSerialValidation && normalizedEnteredSerials.length > 0;
  const receivingServiceLabels = getReceivingServiceLabels(identifierRequirements, hasExpectedSerialValidation);

  const handleReceive = () => {
    if (!canReceive) {
      toast.error('Provide the required identifiers for the selected receive quantity.');
      return;
    }

    let assets = [];

    if (lineItem?.assets?.length > 0) {
      if (hasExpectedSerialValidation && !isOverride) {
        assets = lineItem.assets.filter(
          (asset) => asset.serialNumber && normalizedEnteredSerials.includes(asset.serialNumber)
        );
      } else {
        assets = lineItem.assets.slice(0, requestedQuantity);
      }
    }

    onReceive({
      assets,
      quantityToReceive: requestedQuantity,
      serialNumbers: normalizedEnteredSerials,
      macAddresses: normalizedEnteredMacs
    });
  };

  const handleHold = () => {
    if (!canHold) {
      toast.error('Enter at least one valid serial number to place items on hold.');
      return;
    }

    const assetsToHold = lineItem.assets.filter(
      (asset) => asset.serialNumber && normalizedEnteredSerials.includes(asset.serialNumber)
    );

    onHold(assetsToHold);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" role="dialog" aria-modal="true" aria-labelledby="serial-modal-title">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 id="serial-modal-title" className="text-xl font-bold">
            Capture Receiving Identifiers
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close dialog">
            <FiX size={24} />
          </button>
        </div>

        <div className="mb-4 text-sm">
          <p><strong>Item:</strong> {lineItem?.productName}</p>
          <p><strong>SKU:</strong> {lineItem?.productSku}</p>
          <p><strong>Available Quantity:</strong> {availableQuantity}</p>
        </div>

        {receivingServiceLabels.length > 0 && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-900">Required Receiving Services</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {receivingServiceLabels.map((label) => (
                <span key={label} className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Receive Quantity</label>
          <input
            type="number"
            min="1"
            max={availableQuantity}
            value={receivedQuantity}
            onChange={(e) => setReceivedQuantity(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
            placeholder={`Max: ${availableQuantity}`}
            disabled={isLoading}
          />
          <p className="mt-1 text-xs text-gray-500">
            Leave empty to receive all available units.
          </p>
        </div>

        <div className="flex-grow space-y-4 overflow-y-auto pr-2">
          <BulkIdentifierInput
            label={requireSerials ? 'Serial Numbers (Required)' : 'Serial Numbers'}
            values={enteredSerials}
            onChange={setEnteredSerials}
            maxCount={requestedQuantity}
            placeholder="Enter serial number"
          />

          {hasExpectedSerialValidation && normalizedEnteredSerials.length > 0 && (
            <div className="rounded-md border bg-gray-50 p-2">
              <ul>
                {normalizedEnteredSerials.map((serial) => (
                  <li key={serial} className="flex items-center justify-between border-b p-1.5 text-sm last:border-b-0">
                    <span className="flex items-center">
                      {validationStatus[serial] ? (
                        <FiCheck className="mr-2 text-green-500" />
                      ) : (
                        <FiAlertTriangle className="mr-2 text-red-500" />
                      )}
                      {serial}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {requireMacs && (
            <BulkIdentifierInput
              label="MAC Addresses (Required)"
              values={enteredMacs}
              onChange={setEnteredMacs}
              maxCount={requestedQuantity}
              placeholder="Enter MAC address"
            />
          )}

          <p className="text-xs text-gray-500">
            Serials: {normalizedEnteredSerials.length}/{requestedQuantity}
            {requireMacs ? ` • MACs: ${normalizedEnteredMacs.length}/${requestedQuantity}` : ''}
          </p>

          {canOverrideValidation && (
            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isOverride}
                  onChange={(e) => setIsOverride(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                  disabled={isLoading}
                />
                <span className="ml-2 text-sm text-gray-700">Override Expected-Serial Validation</span>
              </label>
              <p className="ml-6 text-xs text-gray-500">
                Use only when the line is sealed and you need to receive without matching the preloaded serial list.
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3 border-t pt-4">
          <button onClick={onClose} className="rounded-md border border-gray-300 bg-white px-4 py-2 shadow-sm" disabled={isLoading}>
            Cancel
          </button>
          <button
            onClick={handleHold}
            className="rounded-md bg-yellow-500 px-4 py-2 text-white shadow-sm hover:bg-yellow-600 disabled:opacity-50"
            disabled={isLoading || !canHold}
          >
            Place Scanned on Hold
          </button>
          <button
            onClick={handleReceive}
            className="rounded-md bg-green-600 px-4 py-2 text-white shadow-sm hover:bg-green-700 disabled:opacity-50"
            disabled={isLoading || !canReceive}
          >
            {isLoading ? 'Processing...' : `Receive ${requestedQuantity} Items`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SerialNumberModal;
